// The Events drawer of the player page (spec/dashboard.md section 11.2; spec/api-conventions.md section 21.7): the player's
// raw analytics events, newest first, `GET …/players/{player}/events` with a name filter (applied on Enter or Filter, never
// per keystroke) and Load more by cursor; each row shows its params as JSON.
import { useEffect, useState, type FormEvent } from 'react';
import { ops, type ResponseOf } from '../../api/operations';
import type { Player } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { playerPrefix } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useQuery } from '../../workspace/cache';
import { errorText } from './commands';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { Field } from '../../ui/Field';
import { Activity, Filter } from '../../ui/icons';

type EventsPage = ResponseOf<typeof ops.listPlayerEvents>;
type PlayerEvent = EventsPage['items'][number];

const PAGE_SIZE = 50;

export function EventsDrawer({ env, player, open, onClose }: { env: string; player: Player; open: boolean; onClose: () => void }) {
  return (
    <Modal title="Analytics events · newest first" open={open} onClose={onClose} drawer size="l" className="history-drawer-modal">
      {open && <EventsBody env={env} player={player} />}
    </Modal>
  );
}

function EventsBody({ env, player }: { env: string; player: Player }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const [draft, setDraft] = useState('');
  const [name, setName] = useState('');
  const path = { org, game: game.slug, env, player: player.id };
  const query = (cursor?: string) => ({ limit: PAGE_SIZE, ...(name ? { name } : {}), ...(cursor ? { cursor } : {}) });
  const first = useQuery(`${playerPrefix(game.id, env, player.id)}:events:${name}`, () => client.data(ops.listPlayerEvents, { path, query: query() }));
  const [more, setMore] = useState<{ items: PlayerEvent[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  useEffect(() => {
    setMore(null);
    setMoreError(null);
  }, [first.data]);
  const rows = [...(first.data?.items ?? []), ...(more?.items ?? [])];
  const cursor = more ? more.cursor : (first.data?.nextCursor ?? null);
  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await client.data(ops.listPlayerEvents, { path, query: query(cursor) });
      setMore((m) => ({ items: [...(m?.items ?? []), ...page.items], cursor: page.nextCursor }));
    } catch (e) {
      setMoreError(errorText(e));
    } finally {
      setLoadingMore(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setName(draft.trim());
  };
  return (
    <div className="history-drawer events-drawer" data-testid="events-drawer">
      <form className="page-toolbar history-head" onSubmit={submit} role="search" aria-label="Filter the events">
        <Field label="Event name" inline className="events-name">
          <TextInput size="s" aria-label="Event name" placeholder="An exact event name (session.started, custom.level_up …)" value={draft} onChange={(e) => setDraft(e.target.value)} />
        </Field>
        <Button size="s" type="submit" icon={<Filter aria-hidden="true" />}>
          Filter
        </Button>
        {name && (
          <Button
            size="s"
            variant="ghost"
            onClick={() => {
              setDraft('');
              setName('');
            }}
          >
            Every name
          </Button>
        )}
        <span className="spacer" />
        <span className="muted small">the last 30 game-time days, within the environment's retention</span>
      </form>
      {first.error ? (
        <LoadError>{errorText(first.error)}</LoadError>
      ) : first.loading && !first.data ? (
        <SkeletonRows rows={4} label="Loading the events" />
      ) : rows.length === 0 ? (
        <div className="empty" data-testid="events-empty">
          <span className="empty-icon" aria-hidden="true">
            <Activity />
          </span>
          <h3>{name ? `No "${name}" event.` : 'No analytics event recorded for this player.'}</h3>
        </div>
      ) : (
        <ul className="history-list" aria-label="Events">
          {rows.map((ev) => (
            <li key={ev.id} className="history-row" data-testid={`event-${ev.id}`}>
              <div className="history-row-main">
                <span className="history-time">{formatInstant(ev.time, zone, { offsets }, { seconds: true })}</span>
                <code className="history-path">{ev.name}</code>
                {ev.tester && <Badge tone="warning">tester</Badge>}
                <span className="spacer" />
                <span className="muted small">{[ev.platform, ev.appVersion, ev.sessionId ? `session ${ev.sessionId.slice(-8)}` : null].filter(Boolean).join(' · ')}</span>
              </div>
              {ev.params !== null && ev.params !== undefined && <pre className="version-value event-params">{JSON.stringify(ev.params, null, 2)}</pre>}
            </li>
          ))}
        </ul>
      )}
      {cursor && (
        <div className="row drawer-more">
          <Button size="s" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
      {moreError && (
        <LoadError>{moreError}</LoadError>
      )}
    </div>
  );
}
